import { useMemo, useContext, useCallback } from 'react';
import { useSlate } from 'slate-react';
import { Editor as SlateEditor } from 'slate';
import { TagWithMetadata } from 'app/api/tag-queries/types';
import { useContinueTag, useUpdateTag, useCreateTag } from 'app/api/tag-queries/tags';
import { useConcept } from 'app/api/tag-queries/taxonomy';
import { useDocumentTaxonomyConfig } from 'app/api/tag-queries/document-taxonomy-config';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { createAndInsertTag, getSelectionElementId } from 'app/slate/extensions/tag';
import { applyBlockTagBinding } from 'app/slate/extensions/tag/utils/blockTagging';
import { TagContext } from 'app/state/contexts/TagContext';
import { getTagType } from 'app/utils/tag-utils';
import { isEscapedConceptType } from 'app/utils/tag-utils/getConceptNarrowType';
import { findChainInsertionPoint } from './tagChainUtils';
import { useApplyCellTags } from './useApplyCellTags';
import { BlockScope, ScopeAxis } from './ScopeToggle';

interface UseTagInfoContinuationParams {
  selectedTag: TagWithMetadata | null;
  isContentMarked: boolean;
  documentTags: TagWithMetadata[];
  onCancel?: () => void;
}

export const useTagInfoContinuation = ({ selectedTag, isContentMarked, documentTags, onCancel }: UseTagInfoContinuationParams) => {
  const { mutate: createTag } = useCreateTag();
  const { mutate: continueTag } = useContinueTag();
  const { mutate: updateTag } = useUpdateTag();
  const { pDocument, activeSection, section, locale } = useEditorSelector((editor: any) => editor);
  const conceptName = selectedTag?.concept?.name ?? null;
  const { data: conceptData } = useConcept(conceptName, pDocument?.superId);
  const { draftTag, tableSelection, blockSelection, blockScope, setBlockScope, clearBlockSelection, clearTableTagSelection } = useContext(TagContext);
  const { data: config } = useDocumentTaxonomyConfig(pDocument?.superId);
  const taxonomyId = config?.taxonomySelections?.find((s) => s.isActive)?.taxonomy?.id ?? null;
  const editor = useSlate();
  const applyCellTags = useApplyCellTags();

  const hasTableSelection = !!tableSelection?.cells?.length;
  const isProseSelection = (blockSelection?.kind === 'paragraph' || blockSelection?.kind === 'list') && !hasTableSelection;
  const hasImageTarget = blockSelection?.kind === 'image' && !hasTableSelection;

  const isSourceBlockTag = !!selectedTag?.metadata?.blockCarrier;
  const isNumericSource = selectedTag?.type === 'nonFraction';

  const scopeAxis: ScopeAxis = isProseSelection ? 'prose' : 'table';
  const conceptType = (conceptData?.conceptType ?? selectedTag?.concept?.conceptType) as any;
  const scopeLocked: BlockScope | null = isSourceBlockTag
    ? isProseSelection
      ? 'block'
      : 'table'
    : isNumericSource
    ? isProseSelection
      ? 'text'
      : 'cell'
    : isProseSelection
    ? isEscapedConceptType(conceptType)
      ? 'block'
      : 'text'
    : null;
  const scope: BlockScope = scopeLocked ?? blockScope ?? (isProseSelection ? 'text' : 'cell');
  const showScopeToggle = (hasTableSelection || isProseSelection) && scopeLocked === null;

  const proseWholeTarget = isProseSelection && scope === 'block';
  const hasTextTarget = isContentMarked && !hasTableSelection && (!blockSelection || (isProseSelection && !proseWholeTarget));

  const conceptId = conceptData?.id ?? selectedTag?.conceptId ?? '';
  const sectionContext = {
    documentSuperId: pDocument?.superId,
    sectionSuperId: section?.superId,
    pageId: activeSection?.uuid,
  };

  const selectionElementId = useMemo(() => {
    return getSelectionElementId(editor) ?? getSelectionElementId({ ...editor, selection: (editor as any).lastSelection }) ?? draftTag?.onId ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, editor.selection, (editor as any).lastSelection, draftTag?.onId]);

  const tableBlockCarrier = useMemo((): { path: any; elementId: string } | null => {
    const el = tableSelection?.element as any;
    if (!el?.uuid) return null;
    try {
      const ReactEditorLazy = require('slate-react').ReactEditor;
      return { path: ReactEditorLazy.findPath(editor, el), elementId: el.uuid };
    } catch {
      return null;
    }
  }, [tableSelection, editor]);

  const tableWholeTarget = hasTableSelection && scope === 'table' && !!tableBlockCarrier;
  const tableCellTarget = hasTableSelection && scope === 'cell';
  const hasTarget = hasTextTarget || hasImageTarget || hasTableSelection || proseWholeTarget;

  const targetChainable = hasTextTarget || hasImageTarget || tableWholeTarget || proseWholeTarget;
  const canContinue = !isNumericSource && targetChainable;
  const continueDisabledHint = !isNumericSource && tableCellTarget ? 'tag.footer.cell.no.chain' : null;

  const existingTagsWithSameConcept = useMemo(() => {
    if (!selectedTag || !conceptName || !documentTags) return [];
    return documentTags.filter((t) => t.concept?.name === conceptName);
  }, [selectedTag, conceptName, documentTags]);

  const cellTagType = useMemo(() => {
    if (!selectedTag) return 'nonFraction';
    const baseXsdType = selectedTag.concept?.conceptType?.baseXsdType ?? (selectedTag.concept?.conceptType as any)?.baseXbrliType;
    return selectedTag.type ?? (baseXsdType ? getTagType(baseXsdType) : 'nonFraction');
  }, [selectedTag]);

  // --- Continuation handlers (write a nextSegment link) ----------------------

  const continueIntoBlock = useCallback(
    (carrier: { path: any; elementId: string }) => {
      const sections = pDocument?.content?.sections ?? [];
      const { predecessor, successor } = findChainInsertionPoint(existingTagsWithSameConcept, editor, section.superId, sections, carrier.path);
      if (!predecessor && !successor) return;

      continueTag(
        {
          taxonomyId: taxonomyId ?? '',
          ...sectionContext,
          onId: carrier.elementId,
          conceptId,
          lang: locale,
          type: selectedTag?.type ?? 'nonNumeric',
          linkroleUri: selectedTag?.linkroleUri ?? null, // inherit the source tag's role (a chain is one fact)
          predecessorId: predecessor?.id,
          successorId: successor?.id,
        } as any,
        {
          onSuccess: (created: any) => {
            if (created?.id) applyBlockTagBinding(editor, carrier.path, { tagId: created.id, continuedAt: successor?.id ?? undefined });
            clearBlockSelection?.();
            clearTableTagSelection?.();
          },
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      existingTagsWithSameConcept,
      editor,
      pDocument,
      section,
      activeSection,
      conceptId,
      taxonomyId,
      locale,
      selectedTag,
      continueTag,
      clearBlockSelection,
      clearTableTagSelection,
    ]
  );

  const handleTextContinuation = useCallback(() => {
    if (!selectedTag || !isContentMarked || existingTagsWithSameConcept.length === 0) return;
    const sections = pDocument?.content?.sections ?? [];
    const { predecessor, successor } = findChainInsertionPoint(existingTagsWithSameConcept, editor, section.superId, sections);
    if (!predecessor && !successor) return;

    const selectedText = editor.selection ? SlateEditor.string(editor, editor.selection) : undefined;
    createAndInsertTag({
      editor,
      tagType: conceptName ?? '',
      tagData: {
        taxonomyId: taxonomyId ?? '',
        ...sectionContext,
        onId: selectionElementId,
        conceptId,
        lang: locale,
        type: selectedTag.type ?? 'nonNumeric',
        value: selectedText || undefined,
        linkroleUri: selectedTag.linkroleUri ?? null, // inherit the source tag's role
      },
      continuationTag: predecessor ? { tagId: predecessor.id, tagType: predecessor.concept?.name ?? conceptName ?? '' } : null,
      successorTag: successor ? { tagId: successor.id } : null,
      createTagMutation: createTag,
      continueTagMutation: continueTag,
      updateTagMutation: updateTag,
      callbacks: {},
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedTag,
    isContentMarked,
    existingTagsWithSameConcept,
    editor,
    pDocument,
    section,
    activeSection,
    selectionElementId,
    conceptName,
    conceptId,
    taxonomyId,
    locale,
    createTag,
    continueTag,
    updateTag,
  ]);

  const handleContinuation = useCallback(() => {
    if (!canContinue) return;
    if (tableWholeTarget && tableBlockCarrier) {
      continueIntoBlock(tableBlockCarrier);
    } else if ((hasImageTarget || proseWholeTarget) && blockSelection) {
      continueIntoBlock({ path: blockSelection.path, elementId: blockSelection.elementId });
    } else {
      handleTextContinuation();
    }
  }, [canContinue, tableWholeTarget, tableBlockCarrier, hasImageTarget, proseWholeTarget, blockSelection, continueIntoBlock, handleTextContinuation]);

  const createStandaloneBlock = useCallback(
    (carrier: { path: any; elementId: string }) => {
      createTag(
        {
          taxonomyId: taxonomyId ?? '',
          ...sectionContext,
          onId: carrier.elementId,
          conceptId,
          lang: locale,
          type: 'nonNumeric',
          linkroleUri: selectedTag?.linkroleUri ?? null, // inherit the source tag's role
        } as any,
        {
          onSuccess: (created: any) => {
            if (created?.id) applyBlockTagBinding(editor, carrier.path, { tagId: created.id });
            clearBlockSelection?.();
            clearTableTagSelection?.();
          },
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor, conceptId, taxonomyId, locale, createTag, clearBlockSelection, clearTableTagSelection, activeSection, section, pDocument]
  );

  const standaloneIntoCells = useCallback(async () => {
    if (!tableSelection?.cells?.length) return;
    await applyCellTags({
      mode: 'create',
      cells: tableSelection.cells,
      referenceDataSuperId: tableSelection.referenceDataSuperId,
      conceptId,
      linkroleUri: selectedTag?.linkroleUri ?? null, // inherit the source tag's role
      type: cellTagType,
      taxonomyId: taxonomyId ?? '',
      sectionContext,
      locale,
      onTagsCreated: tableSelection.onTagsCreated,
      blockedTagIds: new Set(existingTagsWithSameConcept.map((t) => t.id)),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableSelection, conceptId, cellTagType, taxonomyId, locale, applyCellTags, existingTagsWithSameConcept, activeSection, section, pDocument]);

  const handleStandalone = useCallback(() => {
    if (tableCellTarget) {
      void standaloneIntoCells();
    } else if (tableWholeTarget && tableBlockCarrier) {
      createStandaloneBlock(tableBlockCarrier);
    } else if ((hasImageTarget || proseWholeTarget) && blockSelection) {
      createStandaloneBlock({ path: blockSelection.path, elementId: blockSelection.elementId });
    } else if (hasTextTarget) {
      const selectedText = editor.selection ? SlateEditor.string(editor, editor.selection) : undefined;
      createAndInsertTag({
        editor,
        tagType: conceptName ?? '',
        tagData: {
          taxonomyId: taxonomyId ?? '',
          ...sectionContext,
          onId: selectionElementId,
          conceptId,
          lang: locale,
          type: selectedTag?.type ?? 'nonNumeric',
          value: selectedText || undefined,
        },
        continuationTag: null,
        createTagMutation: createTag,
        continueTagMutation: continueTag,
        updateTagMutation: updateTag,
        callbacks: {},
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tableCellTarget,
    tableWholeTarget,
    tableBlockCarrier,
    hasImageTarget,
    proseWholeTarget,
    blockSelection,
    hasTextTarget,
    standaloneIntoCells,
    createStandaloneBlock,
    editor,
    conceptName,
    conceptId,
    taxonomyId,
    locale,
    selectionElementId,
    selectedTag,
    createTag,
    continueTag,
    updateTag,
  ]);

  const handleCancel = useCallback(() => {
    clearTableTagSelection?.();
    clearBlockSelection?.();
    onCancel?.();
  }, [clearTableTagSelection, clearBlockSelection, onCancel]);

  return {
    handleContinuation,
    handleStandalone,
    handleCancel,
    hasTarget,
    canContinue,
    continueDisabledHint,
    showScopeToggle,
    scopeAxis,
    scope,
    setScope: setBlockScope,
  };
};
